/**
 * Billing API client — typed fetchers for plans + checkout.
 *
 * GET /plans returns the active Plan list (envelope-unwrapped by apiRequest).
 * POST /billing/checkout creates a PaymentOrder (Click) and returns the pay URL.
 */
import { apiBaseUrl, apiRequest } from './api';
import { getMetaContext } from './meta-cookies';
import { gaContext } from './ga-client-id';
import { uuid } from './uuid';
import type { PaymentType } from './payment-type';

export interface Plan {
  id: string;
  key: string;
  name: string;
  /** e.g. "yearly" / "year" — used to label the price period. */
  billingPeriod: string;
  priceUsd: number;
  /** The SUBSCRIPTION price — what auto-renewal charges, per period. */
  priceUzs: number;
  /**
   * What a SINGLE payment for the same period costs: `priceUzs` plus the 10%
   * one-time surcharge, rounded up to a whole thousand so'm.
   *
   * Computed by the API, never here. The pricing card shows both numbers and
   * POST /billing/checkout charges this one, so a client-side percentage would
   * be a second definition of the surcharge — free to disagree with the amount
   * the buyer is actually billed.
   *
   * ⚠️ Nullable ON PURPOSE, though a current API always sends it. The web and
   * the API ship on SEPARATE pipelines gated by `changes:` filters, so a merge
   * touching only `apps/web/**` reaches production against an API that predates
   * this field. Typing it `number` did not make it present — it only stopped
   * `tsc` from asking, and the page rendered «Разовый платёж — null сум» with a
   * green typecheck. Callers must handle the absence, and quote NO price rather
   * than a locally derived one.
   */
  priceUzsOneTime: number | null;
  /** Contact-open allowance per billing period. */
  contactOpenAllowance: number;
  /** Whether the plan grants the full paid product. Shipped by the API rather
   *  than inferred here — see the note on `isPro` in routes/plans.tsx. */
  fullAccess: boolean;
  /** Tender-budget ceiling in so'm, null when unlimited. */
  maxTenderBudgetUzs: number | null;
  /** Max number of categories, or null when unlimited. */
  categoryLimit: number | null;
  exportEnabled: boolean;
  analyticsEnabled: boolean;
  /** Seats included in the plan (1 for individual tiers, 3 for «Бизнес»). */
  seatLimit: number;
  /** This plan cannot be bought without an ИНН on file. Only `ip-monthly` sets
   *  it, and that is the cheapest tier — so the buyer least likely to have an
   *  ИНН is the one most likely to hit it. */
  requiresTin?: boolean;
}

export interface CheckoutResult {
  redirectUrl: string;
  checkoutId: string;
}

/** GET /plans — the active plans, cheapest first (API orders by createdAt). */
export async function fetchPlans(): Promise<Plan[]> {
  return apiRequest<Plan[]>('/plans');
}

/**
 * POST /billing/checkout — create a checkout for `planKey` and get the provider
 * redirect URL (a Click pay URL when PAYMENT_PROVIDER=click). Requires auth
 * (cookie, sent by apiRequest). A unique idempotency-key guards double-submits.
 */
export async function startCheckout(
  planKey: string,
  provider?: string,
  metaEventId?: string,
  /** Which CTA led here — stamped on the server's `checkout_started`. */
  source?: string,
  /**
   * `'guest'` — this order belongs to a buyer who typed their address on the
   * payment sheet itself. It suppresses the SERVER-side InitiateCheckout only
   * (see billing.controller): since 2026-09-22 the order is created in the
   * background as the sheet opens, so a server IC here would fire on "opened
   * the sheet" — the very signal this change takes away from Meta. The browser
   * fires the deduped Pixel+CAPI pair on the qualified gesture instead.
   *
   * Nothing else about the request changes; `checkout_started` is untouched.
   */
  via?: 'guest',
): Promise<CheckoutResult> {
  return apiRequest<CheckoutResult>('/billing/checkout', {
    method: 'POST',
    headers: { 'idempotency-key': uuid() },
    // Attach the chosen method + dedup id + Meta tracking context so the
    // server-side CAPI fires InitiateCheckout matched to the browser Pixel event.
    // GA context alongside the Meta one: the purchase is sent server-side from
    // a webhook with no browser, so GA4's client_id has to be captured here.
    body: JSON.stringify({
      planKey,
      provider,
      metaEventId,
      source,
      via,
      ...getMetaContext(),
      ...(await gaContext()),
    }),
  });
}

/** What the on-site Stripe card form needs (Kazakhstan, Stripe Elements). */
export interface StripeSubscriptionIntent {
  /** Our order — the buyer lands on `/billing/return?order=<orderId>` after paying. */
  orderId: string;
  /** The first invoice's PaymentIntent client secret, for `stripe.confirmPayment`. */
  clientSecret: string;
  subscriptionId: string;
}

/**
 * POST /billing/stripe-subscription-intent `{ stage: 'order' }` — Kazakhstan's
 * card form ON OUR SITE has OPENED («deferred intent», 2026-10-20).
 *
 * The same Stripe sale as `startCheckout(plan, 'stripe')` — same guards, same
 * order, same InitiateCheckout under `metaEventId`, same `checkout_started` —
 * but nothing is created at Stripe yet: the card fields are already on screen
 * (`<Elements mode: 'subscription'>`), and the subscription waits for
 * «Оплатить» (`subscribeStripeOrder`). A fresh idempotency key per call: each
 * modal opening is its own attempt; only a double tap folds, and that one
 * answers `duplicate: true` with no order.
 */
export async function openStripeOrder(
  planKey: string,
  metaEventId?: string,
  source?: string,
): Promise<{ orderId: string; clientSecret: null } | { duplicate: true; clientSecret: null }> {
  return apiRequest<
    { orderId: string; clientSecret: null } | { duplicate: true; clientSecret: null }
  >('/billing/stripe-subscription-intent', {
    method: 'POST',
    headers: { 'idempotency-key': uuid() },
    body: JSON.stringify({
      planKey,
      provider: 'stripe',
      stage: 'order',
      metaEventId,
      source,
      ...getMetaContext(),
      ...(await gaContext()),
    }),
  });
}

/**
 * POST /billing/stripe-subscription-intent `{ orderId }` — «Оплатить» pressed:
 * the server creates the Stripe Customer and the unpaid subscription for the
 * order `openStripeOrder` wrote and answers its first invoice's client secret,
 * which `StripeCardModal` confirms with `stripe.confirmPayment`. Called once
 * per opening (the secret is reused for a second card); a repeat for the same
 * order gets the same subscription back.
 */
export async function subscribeStripeOrder(
  planKey: string,
  orderId: string,
): Promise<StripeSubscriptionIntent> {
  return apiRequest<StripeSubscriptionIntent>('/billing/stripe-subscription-intent', {
    method: 'POST',
    body: JSON.stringify({ planKey, provider: 'stripe', orderId }),
  });
}

/**
 * POST /billing/subscribe-octo — start an OCTO subscribe: the server creates an
 * `octo-subscribe` order at the base plan price and returns the Octobank hosted
 * page URL. The caller redirects to it; the card is entered + tokenised there,
 * and the webhook activates + turns auto-renew on. OCTO-allowlist gated.
 */
export async function subscribeOcto(
  planKey: string,
  metaEventId?: string,
  /** `'guest'` — see `startCheckout`. This endpoint fires no server-side
   *  InitiateCheckout today, so the flag is carried for symmetry and for the
   *  day one is added: both hosted creators must agree about guests. */
  via?: 'guest',
): Promise<{ redirectUrl: string; checkoutId: string }> {
  return apiRequest<{ redirectUrl: string; checkoutId: string }>('/billing/subscribe-octo', {
    method: 'POST',
    body: JSON.stringify({
      planKey,
      metaEventId,
      via,
      ...getMetaContext(),
      ...(await gaContext()),
    }),
  });
}

/**
 * POST /billing/checkout-link — prepare the pay TAP without creating an order
 * (owner, 2026-09-30: «заказ больше не надо заранее создавать»).
 *
 * The server checks what the order would need (plan, method, ИНН), stores the
 * request, and answers a token plus the id the order WILL have. The returned
 * `redirectUrl` is OUR endpoint, `GET /billing/checkout-link/:token`: the
 * sheet's pay control is an `<a href>` to it, so following it is still the user
 * gesture an in-app WebView honours, and the order is created by the server at
 * that moment before it answers 302 to Payme / Click / Octobank. Same shape as
 * `startCheckout` / `subscribeOcto`, so `usePreparedCheckout` takes either.
 *
 * `checkoutId` is the RESERVED order id — the events the sheet writes name it,
 * and the order the tap creates is written under it.
 */
export async function prepareCheckoutLink(
  kind: 'hosted' | 'octo',
  planKey: string,
  provider: string | undefined,
  metaEventId: string | undefined,
  source: string | undefined,
  /** `'guest'` — same meaning as on `startCheckout`; stored with the request. */
  via?: 'guest',
): Promise<{ redirectUrl: string; checkoutId: string }> {
  const { token, checkoutId } = await apiRequest<{ token: string; checkoutId: string }>(
    '/billing/checkout-link',
    {
      method: 'POST',
      body: JSON.stringify({
        kind,
        planKey,
        provider,
        metaEventId,
        source,
        via,
        ...getMetaContext(),
        ...(await gaContext()),
      }),
    },
  );
  return {
    redirectUrl: `${apiBaseUrl}/billing/checkout-link/${encodeURIComponent(token)}`,
    checkoutId,
  };
}

/** GET /billing/methods — the user-selectable payment methods (e.g. ["payme","octo"]). */
export interface PaymentMethodsState {
  methods: string[];
  /** Whether card-on-file auto-renewal is offered to this user (staged rollout). */
  recurringEnabled: boolean;
  /** Whether test plans (`test-*`) are shown to this user as normal tariffs. */
  testPlansEnabled: boolean;
}

export async function fetchPaymentMethods(): Promise<PaymentMethodsState> {
  return apiRequest<PaymentMethodsState>('/billing/methods');
}

/**
 * GET /billing/subscribe-hint — personalisation for the subscribe modal.
 *
 * `nicheHint` is a live count for the value pill (open lots in the buyer's saved
 * searches, or the catalogue's daily pace when they have none); `null` when
 * there is nothing worth showing. `reminderChannel` is the channel the renewal
 * reminder will actually use, so the modal's timeline names the right one.
 */
export interface SubscribeHint {
  nicheHint: { tier: 'niche' | 'direction' | 'daily'; count: number } | null;
  reminderChannel: 'telegram' | 'email';
}

export async function fetchSubscribeHint(): Promise<SubscribeHint> {
  return apiRequest<SubscribeHint>('/billing/subscribe-hint');
}

/**
 * The browser leg of the confirmed-purchase conversion, as the SERVER computed
 * it (GET /billing/conversion). `null` when there is nothing to report — no
 * recent completed order, an internal account, or outside the echo window.
 *
 * Every field is authoritative and passed straight to `trackPurchaseConfirmed`.
 * In particular `eventId` is the server's own dedup id: re-deriving it in the
 * browser is what would make Meta count one purchase twice.
 */
export interface PurchaseConversion {
  eventName: 'Purchase' | 'Subscribe';
  eventId: string;
  valueUzs: number;
  currency: string;
  planKey: string;
  /**
   * annual / subscription / onetime — the `payment_type` the SERVER leg of this
   * purchase carries, derived from the order's rail and the plan's period.
   * Echoed, never recomputed: a deduplicated pair keeps one leg's custom_data.
   * Optional because web and API ship separately — an older API omits it, and
   * then the browser leg sends no `payment_type` rather than a guess.
   */
  paymentType?: PaymentType;
  /** The `_fbc` click id stored on the order at checkout, re-seated into the
   *  cookie before the browser leg fires when the live one is gone. Null when
   *  the buyer never arrived via a Meta ad click. */
  fbc: string | null;
}

export async function fetchPurchaseConversion(): Promise<PurchaseConversion | null> {
  const res = await apiRequest<{ conversion: PurchaseConversion | null }>('/billing/conversion');
  return res.conversion;
}

// ─── Card-on-file / auto-renewal (OCTO) ─────────────────────────────────────

/** The caller's saved card + auto-renew flag (GET /billing/card). */
export interface SavedCardState {
  /** Masked card number for display (e.g. "8600 06** **** 6311"), or null. */
  card: { maskedPan: string } | null;
  autoRenew: boolean;
  /** Whether card-on-file auto-renewal is offered to this user (staged rollout). */
  recurringEnabled: boolean;
  /**
   * The charger turned auto-renew off after one or more failed charges this
   * period (card declined / expired). Drives the "update your card" recovery
   * banner. False when the user turned auto-renew off themselves.
   */
  renewalFailed: boolean;
  /**
   * «Пауза на 1 месяц» may be offered on the cancel sheet: auto-renew on, never
   * paused, no failed charge this period (2026-10-09). Optional until every API
   * in a tab's lifetime returns it — absent reads as «not offered».
   */
  pauseAvailable?: boolean;
  /**
   * The window «Пауза на 1 месяц» would set, when it is offered: Pro closes on
   * `startsAt` (the period end) and reopens with the charge on `until`. ISO
   * strings. Absent/null → quote no dates.
   */
  pauseOffer?: { startsAt: string; until: string } | null;
  /** A scheduled (`startsAt` ahead) or running pause — the cabinet's «Пауза до …»
   *  line and «Возобновить сейчас». Absent/null → none. */
  pause?: { startsAt: string; until: string } | null;
}

/** GET /billing/card — the saved card-on-file + auto-renew state (auth). */
export async function fetchSavedCard(): Promise<SavedCardState> {
  return apiRequest<SavedCardState>('/billing/card');
}

/** One row of the caller's payment history (GET /billing/history). */
export interface PaymentHistoryItem {
  id: string;
  /** ISO timestamp — when paid, else when the order was opened. */
  date: string;
  amountUzs: number;
  /** PaymentOrderStatus: 'completed' | 'pending' | 'cancelled' | … */
  status: string;
  /** Gateway used ('payme' | 'octo' | 'click'); auto-renewals collapse to 'payme'. */
  method: string;
  /** OCTO fiscal cheque (ofd.soliq.uz) link when one exists — the buyer's official
   *  receipt. Absent for Payme/Click and orders paid before fiscalization. */
  fiscalReceiptUrl?: string;
  /**
   * Why a `cancelled` order was refused, as a CODE in the `checkout_failed`
   * vocabulary (`insufficient_funds`, `invalid_card`, `3ds_failed`,
   * `card_expired`, `declined`, `payme_<code>`, …) — never a sentence to print.
   * Absent on orders cancelled before the gateway started reporting it
   * (2026-09-14) and on every non-cancelled order.
   */
  cancelReason?: string;
}

/** GET /billing/history — the caller's recent payment orders, newest first (auth). */
export async function fetchPaymentHistory(): Promise<PaymentHistoryItem[]> {
  return apiRequest<PaymentHistoryItem[]>('/billing/history');
}

/**
 * POST /billing/cards/otp-cooldown — reserve this card token's one SMS slot
 * before asking Payme for a code (auth).
 *
 * Resolves when the code may be requested; throws `ApiError` with
 * `status: 429` / `code: 'otp_cooldown'` when one was already requested for
 * this token seconds ago. Everything else a caller gets is an ordinary failure
 * — and, since the SMS is requested from Payme by the browser and not through
 * us, one the caller should IGNORE rather than let cost the buyer his code.
 */
export async function claimOtpCooldown(token: string): Promise<void> {
  await apiRequest<{ ok: true }>('/billing/cards/otp-cooldown', {
    method: 'POST',
    body: JSON.stringify({ token }),
  });
}

/**
 * POST /billing/cards — store a browser-verified Payme card token (auth). The
 * card was tokenized + OTP-verified directly with Payme in the browser (SAQ
 * A-EP); we send only the verified token + masked PAN.
 */
export async function saveCard(input: { token: string; maskedPan: string }): Promise<{
  card: { maskedPan: string };
}> {
  return apiRequest('/billing/cards', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * POST /billing/subscribe-card — one-step subscribe: charge the first period with
 * the just-verified card, activate the plan, and turn on auto-renewal. For users
 * who aren't paid yet (already-paid users use saveCard instead).
 */
export async function subscribeWithCard(input: {
  token: string;
  maskedPan: string;
  planKey: string;
  /**
   * Dedup id shared with the browser Pixel/GA4 begin-checkout event, so the
   * server-side CAPI InitiateCheckout the subscribe handler fires matches it.
   */
  metaEventId?: string;
  /** Which CTA led here — stamped on the server's `checkout_started`. */
  source?: string;
}): Promise<{ ok: boolean }> {
  // Send the Meta tracking context (Pixel cookies, UTM, page URL, visitor id)
  // alongside — the subscribe path is now the primary checkout, so its CAPI
  // InitiateCheckout needs the same match signals /billing/checkout gets.
  return apiRequest('/billing/subscribe-card', {
    method: 'POST',
    body: JSON.stringify({ ...input, ...getMetaContext(), ...(await gaContext()) }),
  });
}

/**
 * The answer to «Почему отключаете продление?», when the sheet got one.
 *
 * ⚠️ OPTIONAL on the wire, and must stay so. The API validates `reason` against
 * a closed code list and rejects anything else, but a MISSING reason is
 * accepted — the two ship on separate pipelines, and a required field here
 * would mean an older bundle could not cancel at all. See
 * `services/api/src/modules/billing/dto/churn-reason.dto.ts`.
 */
export interface ChurnReasonInput {
  /** A code from `lib/churn-reasons.ts`, never a localized label. */
  reason?: string;
  /** The buyer's own words, ≤300 chars, already trimmed. */
  reasonText?: string;
}

/** Only the keys that carry something — `whitelist`/`forbidNonWhitelisted` on
 *  the API accepts an absent field but an explicit `undefined` serializes away
 *  anyway; this keeps the body minimal and the test assertions readable. */
function churnBody(churn?: ChurnReasonInput): Record<string, string> {
  const body: Record<string, string> = {};
  if (churn?.reason) body.reason = churn.reason;
  if (churn?.reasonText) body.reasonText = churn.reasonText;
  return body;
}

/** POST /billing/auto-renew — toggle auto-renewal on the active subscription.
 *  `churn` is read only on the way OFF; enabling carries no reason. */
export async function setAutoRenew(
  enabled: boolean,
  churn?: ChurnReasonInput,
): Promise<{ autoRenew: boolean }> {
  return apiRequest<{ autoRenew: boolean }>('/billing/auto-renew', {
    method: 'POST',
    body: JSON.stringify({ enabled, ...churnBody(churn) }),
  });
}

/** POST /billing/pause — «Пауза на 1 месяц»: from the period end Pro is closed
 *  for a month and nothing is charged, then the card is charged as usual and
 *  access reopens. Auto-renew stays on. Once per subscription (400 otherwise).
 *  Carries the reason the cancel sheet had selected. */
export interface PauseResult {
  ok: boolean;
  currentPeriodEnd: string;
  pauseStartsAt?: string;
  pausedUntil?: string;
}
export async function pauseSubscription(churn?: ChurnReasonInput): Promise<PauseResult> {
  return apiRequest<PauseResult>('/billing/pause', {
    method: 'POST',
    body: JSON.stringify(churnBody(churn)),
  });
}

/** POST /billing/resume — «Возобновить сейчас»: ends a scheduled pause (back to
 *  the old charge date) or a running one (charged on the charger's next cycle,
 *  Pro reopens at once). 400 when there is no pause. */
export interface ResumeResult {
  ok: boolean;
  currentPeriodEnd: string;
  phase: 'scheduled' | 'running';
}
export async function resumeSubscription(): Promise<ResumeResult> {
  return apiRequest<ResumeResult>('/billing/resume', { method: 'POST' });
}

/** DELETE /billing/card — forget the saved card and turn off auto-renew (auth).
 *  The reason rides in the BODY, same shape as the post above — one gesture,
 *  one wire format (see `dto/remove-card.dto.ts`). */
export async function removeSavedCard(churn?: ChurnReasonInput): Promise<{ ok: boolean }> {
  return apiRequest<{ ok: boolean }>('/billing/card', {
    method: 'DELETE',
    body: JSON.stringify(churnBody(churn)),
  });
}
